'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {enumerate} from 'ts-transformer-enumerate';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import {v4 as uuidV4} from 'uuid';

// prime react imports
import {Button} from 'primereact/button';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {
    Dropdown,
    DropdownChangeEvent,
    DropdownProps
} from 'primereact/dropdown';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';
import {Toolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeSelectionEvent,
    TreeMultipleSelectionKeys
} from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import {TreeTableExpandedKeysType} from 'primereact/treetable';
import {IconUtils} from 'primereact/utils';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation,
    ItemAttributesSettable
} from '~/shared/src/ci/Item';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import Reference, {
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getDataStructurePath,
    getParentDataObject,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';
import {Query} from '~/shared/src/utilities/query';
import {MapDataSet} from '~/shared/src/utilities/mapDataSet';
import {Condition} from '~/shared/src/utilities/condition';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {fieldTypeToItemTypeMapping} from '~/shared/src/utilities/modules/form';
import {FilterConfigSet} from '~/shared/src/utilities/filterConfig';
import {FilterMatchMode} from '~/shared/src/validationSchemes/modules/overview';
import {
    getCustomFilterMode,
    getCustomFilters,
    getDefaultFilterMatchMode,
    getValueStringFromMatchModeAndValue
} from '~/shared/src/utilities/filterUtils';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';

// frontend components import
import getAddItemDialog from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';
import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import FilterField from '~/frontend/src/components/base/shared/field/FilterField';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';

// frontend imports
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getIconSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';


type FilterDefinition = {
    type : 'item' | 'meta' | 'reference' | 'referenceMeta';
    key : string;
    label : string;
    icon : string;
    dataObjectId : number;
    attributePath : string;
    dataDescriptorAlias : string;
    valueType : PropertyValueType;
    params? : FilterConfigSet;
    value? : any;
    operator? : FilterMatchMode;
    conditionIds? : number[];
};

type ExtendTreeNode = Omit<TreeNode.TreeNode, 'data'> & {
    key : string;
    label : string;
    tooltip? : React.ReactNode;
    dataObject? : DataObjectProperties;
    children? : ExtendTreeNode[];
    data : {
        itemId : number;
        isDeleted : boolean;

        isRootFor? : number[];
        rootDataObjectId? : number;
        isLocationFor? : number[];
        locationDataObjectId? : number;
        isConnectToFor? : number[];
        connectToDataObjectId? : number;
        isTimeFor? : number[];
        timeDataObjectId? : number;

        buttons : React.ReactNode[];
        filters : FilterDefinition[];
    };
};

type NodeButtons = {
    [ buttonId : string ] : {
        justInteracted : boolean;
        content? : ContentButtonType;
    };
};

type BuildTreeData = {
    treeData : ExtendTreeNode[],
    expandedKeys : TreeTableExpandedKeysType
};

type ParsedCondition = {
    [ alias : string ] : {
        ids : number[];
        operator? : FilterMatchMode;
        value? : any;
    };
};
type ParsedConditions = {
    [ dataObjectId : number ] : ParsedCondition;
};


export type DefaultProps = {

    readOnly? : boolean;

    treeScrollPositions? : {
        [ queryConditionId : string ] : {
            top : number;
            left : number;
        };
    };

    expandedKeys? : {
        [ queryConditionId : string ] : TreeTableExpandedKeysType;
    };

    selectionKeys? : {
        [ queryConditionId : string ] : TreeMultipleSelectionKeys;
    };

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    tabContainerScrollPosition? : number;
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    treeScrollPositions          : {},
    expandedKeys                 : {},
    selectionKeys                : {},
    additionalElusiveDataContexts: [],
    tabContainerScrollPosition   : 0
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId? : number;

    activeTree? : string;

    selectedElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectElement? : ( id? : number ) => void;

    nodeIdToPanelKeyMap? : {
        [ nodeId : string ] : string
    };
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {
    dataSets : MapDataSet[];
    dataStructure? : DataStructure;
};

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    dataSets? : MapDataSet[];
    treeData? : {
        [ queryConditionId : string ] : {
            order : number;
            dataSets : {
                [ dataSetKey : string ] : {
                    node : ExtendTreeNode;
                    dataSet : MapDataSet;
                };
            };
            tree : ExtendTreeNode[];
        };
    };
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;

const schemata = enumerate<YnSchema>() as Schema[];


const SearchTree = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch                                  = useDispatch(),
          {
              activeTree,
              additionalElusiveDataContexts,
              dataContext,
              expandedKeys,
              nodeIdToPanelKeyMap,
              onSelectElement,
              parentUid,
              readOnly,
              rootItemId,
              selectedElementId,
              selectionKeys,
              tabContainerScrollPosition,
              treeScrollPositions,
              uid
          }                                                       = props,
          user                                                    = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                                 = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                                      = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          elusiveData                                             = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          allElusiveData                                          = useSelector( ( state : StoreState ) => state.elusiveData ),
          changedDataSets                                         = useRef<MapDataSet[]>( [] ),
          lastParams                                              = useRef<any>( null ),
          initialRendered                                         = useRef<boolean>( false ),
          treeElement                                             = useRef<HTMLDivElement | null>( null ),
          contextMenu                                             = useRef<ContextMenu | null>( null ),
          nodeButtons                                             = useRef<NodeButtons>( {} ),
          ignoreSelectionChange                                   = useRef<boolean>( false ),
          paramsToSet                                             = useRef<Partial<AllProps> | null>( null ),
          elusiveDataToSet                                        = useRef<Partial<ElusiveData> | null>( null ),
          [ contextMenuNodeKey, setContextMenuNodeKey ]           = useState<string | undefined>( undefined ),
          [ contextMenuNode, setContextMenuNode ]                 = useState<ExtendTreeNode | null>( null ),
          [ showAddMapDataSetDialog, setShowAddMapDataSetDialog ] = useState<boolean>( false ),
          [ newMapDataSetType, setNewMapDataSetType ]             = useState<'new' | 'forCurrent'>( 'new' ),
          [ newMapDataSetName, setNewMapDataSetName ]             = useState<string>( '' ),
          [ newMapDataSetSchema, setNewMapDataSetSchema ]         = useState<Schema>( 'DATA' ),
          [ newMapDataSetItemType, setNewMapDataSetItemType ]     = useState<string>( '' ),
          language                                                = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                                      = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          rootItem                                                = useMemo(
              () => {
                  let retVal : Item | undefined;

                  if ( rootItemId ) {
                      retVal = global.data[ dataContext ]?.items[ rootItemId ];
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          dataSets                                                = useMemo(
              () => {
                  let retVal = elusiveData?.dataSets ?? [];

                  changedDataSets.current = [];

                  if (
                      rootItem
                      && (
                          !retVal.length
                          || (
                              lastParams.current
                              && (
                                  dataContext !== lastParams.current.dataContext
                                  || rootItem !== lastParams.current.rootItem
                                  || globalDataVersions !== lastParams.current.globalDataVersions
                              )
                          )
                      )
                  ) {
                      retVal = [];

                      const newDataSets = dataExtraction.extractMapDataSetsDataStructureQueryOnly( rootItem, dataContext );

                      if ( newDataSets.length && elusiveData?.dataSets ) {
                          for ( let i = 0; i < newDataSets.length; i++ ) {
                              const oldDataSet = elusiveData?.dataSets.find( dS => dS.data.id === newDataSets[ i ].data.id && JSON.stringify( dS ) === JSON.stringify( newDataSets[ i ] ) );

                              // nothing changed
                              if ( oldDataSet ) {
                                  retVal.push( oldDataSet );
                              }
                              // changed or new
                              else {
                                  retVal.push( newDataSets[ i ] );
                                  changedDataSets.current.push( newDataSets[ i ] );
                              }
                          }
                      }
                      // all new
                      else {
                          retVal = newDataSets;
                          changedDataSets.current = newDataSets;
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItem,
                  globalDataVersions,
                  elusiveData?.dataSets
              ]
          ),
          selectableSchemata                                      = useMemo(
              () => {
                  const retVal : Schema[] = [];

                  if ( user ) {
                      for ( const schema of schemata ) {
                          const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

                          for ( const itemType of itemTypes ) {
                              if (
                                  itemType.id > 0
                                  && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                              ) {
                                  retVal.push( schema );
                                  break;
                              }
                          }
                      }

                      retVal.sort();
                  }

                  return retVal;
              },
              [
                  user
              ]
          ),
          addDataObject                                           = useCallback(
              ( dataObjectToUse : DataObjectProperties, dataStructureToUse : DataStructure, item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) => {
                  const dataObjectSchema       = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        dataObjectItemType     = metaModel[ dataObjectSchema ].getItemType( item.itemTypeId )?.name,
                        dataObjectReferenceSet = metaModel[ dataObjectSchema ].getReferenceSet( reference.referenceSetId );

                  if ( dataStructureToUse && dataObjectItemType && dataObjectReferenceSet ) {
                      const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                            dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                            objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                            objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                            isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                            referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id,
                            referenceDirection                  = dataObjectReferenceSet.srcItemType.name === dataObjectReferenceSet.destItemType.name
                                ? reference.srcItemId
                                    ? 'SrcToDest'
                                    : 'DestToSrc'
                                : dataObjectReferenceSet.srcItemType.name === dataObjectItemType
                                    ? 'SrcToDest'
                                    : 'DestToSrc';

                      const newItem = dispatch( addItem(
                          dataContext,
                          {
                              statusId  : activeStatusId,
                              itemTypeId: dataObjectItemTypeId,
                              key       : `${rootItem!.key}:${dataObjectItemType}:${uuidV4().substring( 0, 8 )}`,
                              name      : dataObjectItemType,
                              properties: [
                                  {
                                      propertySetId: objectTypePropertySetId,
                                      value        : dataObjectItemType,
                                      statusId     : activeStatusId
                                  },
                                  {
                                      propertySetId: objectSchemaPropertySetId,
                                      value        : dataObjectSchema.toLowerCase(),
                                      statusId     : activeStatusId
                                  }
                              ]
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      const parentReference = dispatch( addReference(
                          dataContext,
                          {
                              referenceSetId: isReferencedToReferenceSetId,
                              statusId      : activeStatusId,
                              srcItemId     : newItem.id,
                              destItemId    : dataObjectToUse.origId,
                              enumProperties: [
                                  {
                                      referenceId           : Reference.uniqueIdCount,
                                      referencePropertySetId: referenceConfigurationPropertySetId,
                                      properties            : [
                                          {
                                              referenceId           : Reference.uniqueIdCount,
                                              referencePropertySetId: referenceConfigurationPropertySetId,
                                              key                   : dataObjectReferenceSet?.referenceType.name,
                                              value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                              statusId              : activeStatusId
                                          }
                                      ]
                                  }
                              ]
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      if ( parentReference ) {
                          newItem.registerReference( parentReference );
                      }

                      // select new element
                      onSelectElement?.( newItem.id );
                  }
              },
              [
                  additionalElusiveDataContexts,
                  dataContext
              ]
          ),
          setMapDataSetActive                                     = ( itemToUse : Item, active : boolean ) => {
              dispatch( updateItem(
                  dataContext,
                  itemToUse.id,
                  {
                      properties: {
                          [ metaModel[ itemToUse.schema ].getPropertyType( 'Active' )!.id ]: {
                              value: active
                          }
                      }
                  },
                  additionalElusiveDataContexts
              ) );
          },
          setRoot                                                 = ( dataObjectId : number, mapDataSetId : number ) => {
              const activeStatusId               = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                    dOIsRootForMDSReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'MapDataSet', 'isRootFor', 'src' )![ 0 ].id,
                    existingReferences           = dataUtils.findReferences( dataContext, dOIsRootForMDSReferenceSetId, undefined, mapDataSetId );

              if ( existingReferences?.length ) {
                  for ( const existingReference of existingReferences ) {
                      dispatch( deleteReference(
                          dataContext,
                          existingReference.id,
                          true,
                          additionalElusiveDataContexts,
                          true
                      ) );
                  }
              }

              dispatch( addReference(
                  dataContext,
                  {
                      referenceSetId: dOIsRootForMDSReferenceSetId,
                      statusId      : activeStatusId,
                      srcItemId     : dataObjectId,
                      destItemId    : mapDataSetId
                  },
                  'CFG',
                  additionalElusiveDataContexts
              ) );
          },
          setConnectTo                                            = ( dataObjectId : number, mapDataSetId : number ) => {
              const dataSet = dataSets.find( dS => dS.data.id === mapDataSetId ) as MapDataSet | undefined;

              if ( dataSet?.query.dataStructure ) {
                  const dataStructure = dataSet.query.dataStructure,
                        dataObject    = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.origId === dataObjectId ) as DataStructure | undefined;

                  if ( dataObject ) {
                      const dataObjectSchema   = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                            dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                      if ( dataObjectItemType ) {
                          const coordinatePropertySet = dataObjectItemType.propertySets.find( pS => pS.valueType === 'COORDINATE' );

                          if ( coordinatePropertySet ) {
                              const activeStatusId                              = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                                    connectToAttributePathPropertyTypeId        = metaModel[ 'CFG' ].getPropertyType( 'ConnectToAttributePath' )!.id,
                                    dOProvidesConnectToDataForMDSReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'MapDataSet', 'providesConnectToDataFor', 'src' )![ 0 ].id,
                                    existingReferences                          = dataUtils.findReferences( dataContext, dOProvidesConnectToDataForMDSReferenceSetId, undefined, mapDataSetId );

                              if ( existingReferences?.length ) {
                                  for ( const existingReference of existingReferences ) {
                                      dispatch( deleteReference(
                                          dataContext,
                                          existingReference.id,
                                          true,
                                          additionalElusiveDataContexts,
                                          true
                                      ) );
                                  }
                              }

                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dOProvidesConnectToDataForMDSReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : dataObjectId,
                                      destItemId    : mapDataSetId
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              dispatch( updateItem(
                                  dataContext,
                                  mapDataSetId,
                                  {
                                      properties: {
                                          [ connectToAttributePathPropertyTypeId ]: {
                                              value: `property.${coordinatePropertySet.propertyType.name}`
                                          }
                                      }
                                  }
                              ) );
                          }
                      }
                  }
              }
          },
          setLocation                                             = ( dataObjectId : number, mapDataSetId : number ) => {
              const dataSet = dataSets.find( dS => dS.data.id === mapDataSetId ) as MapDataSet | undefined;

              if ( dataSet?.query.dataStructure ) {
                  const dataStructure = dataSet.query.dataStructure,
                        dataObject    = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.origId === dataObjectId ) as DataStructure | undefined;

                  if ( dataObject ) {
                      const dataObjectSchema   = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                            dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                      if ( dataObjectItemType ) {
                          const coordinatePropertySet = dataObjectItemType.propertySets.find( pS => pS.valueType === 'COORDINATE' );

                          if ( coordinatePropertySet ) {
                              const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                                    locationAttributePathPropertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'LocationAttributePath' )!.id,
                                    dOProvidesDataForMDSReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'MapDataSet', 'providesDataFor', 'src' )![ 0 ].id,
                                    existingReferences                  = dataUtils.findReferences( dataContext, dOProvidesDataForMDSReferenceSetId, undefined, mapDataSetId );

                              if ( existingReferences?.length ) {
                                  for ( const existingReference of existingReferences ) {
                                      dispatch( deleteReference(
                                          dataContext,
                                          existingReference.id,
                                          true,
                                          additionalElusiveDataContexts,
                                          true
                                      ) );
                                  }
                              }

                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dOProvidesDataForMDSReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : dataObjectId,
                                      destItemId    : mapDataSetId
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              dispatch( updateItem(
                                  dataContext,
                                  mapDataSetId,
                                  {
                                      properties: {
                                          [ locationAttributePathPropertyTypeId ]: {
                                              value: `property.${coordinatePropertySet.propertyType.name}`
                                          }
                                      }
                                  }
                              ) );
                          }
                      }
                  }
              }
          },
          setTime                                                 = ( dataObjectId : number, mapDataSetId : number ) => {
              const dataSet = dataSets.find( dS => dS.data.id === mapDataSetId ) as MapDataSet | undefined;

              if ( dataSet?.query.dataStructure ) {
                  const dataStructure = dataSet.query.dataStructure,
                        dataObject    = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.origId === dataObjectId ) as DataStructure | undefined;

                  if ( dataObject ) {
                      const dataObjectSchema   = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                            dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                      if ( dataObjectItemType ) {
                          const timestampPropertySet = dataObjectItemType.propertySets.find( pS => pS.valueType === 'TIMESTAMP' );

                          if ( timestampPropertySet ) {
                              const activeStatusId                         = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                                    timeAttributePathPropertyTypeId        = metaModel[ 'CFG' ].getPropertyType( 'TimeAttributePath' )!.id,
                                    dOProvidesTimeDataForMDSReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'MapDataSet', 'providesTimeDataFor', 'src' )![ 0 ].id,
                                    existingReferences                     = dataUtils.findReferences( dataContext, dOProvidesTimeDataForMDSReferenceSetId, undefined, mapDataSetId );

                              if ( existingReferences?.length ) {
                                  for ( const existingReference of existingReferences ) {
                                      dispatch( deleteReference(
                                          dataContext,
                                          existingReference.id,
                                          true,
                                          additionalElusiveDataContexts,
                                          true
                                      ) );
                                  }
                              }

                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dOProvidesTimeDataForMDSReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : dataObjectId,
                                      destItemId    : mapDataSetId
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              dispatch( updateItem(
                                  dataContext,
                                  mapDataSetId,
                                  {
                                      properties: {
                                          [ timeAttributePathPropertyTypeId ]: {
                                              value: `property.${timestampPropertySet.propertyType.name}`
                                          }
                                      }
                                  }
                              ) );
                          }
                      }
                  }
              }
          },
          deleteOrUndeleteNode                                    = ( nodeId : string, itemToUse : Item ) => {
              const referenceToParentDataObject = global.data[ dataContext ]?.references?.[ itemToUse.references.find(
                        ( referenceId ) => {
                            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                            let retVal : boolean = false;

                            if (
                                reference
                                && reference.srcItemId === itemToUse.id
                                && reference.referenceSet.referenceType.name === 'isReferencedTo'
                            ) {
                                retVal = true
                            }

                            return retVal;
                        }
                    ) ?? 0 ],
                    formPanelKey                = nodeIdToPanelKeyMap?.[ nodeId ]?.replace( '_attributesForm', '' );

              // undelete
              if ( itemToUse.isDeleted() ) {
                  if ( referenceToParentDataObject ) {
                      dispatch( updateReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          { statusId: referenceToParentDataObject.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }

                  dispatch( updateItem(
                      dataContext,
                      itemToUse.id,
                      { statusId: itemToUse.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }

              // delete
              else {
                  if ( referenceToParentDataObject ) {
                      dispatch( deleteReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }

                  dispatch( deleteItem(
                      dataContext,
                      itemToUse.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      false
                  ) );

                  // remove from selectionKeys if
                  if ( activeTree && selectionKeys?.[ activeTree ]?.[ nodeId ] ) {
                      const newSelectionKeys = {
                          ...selectionKeys,
                          [ activeTree ]: { ...selectionKeys?.[ activeTree ] }
                      };

                      delete newSelectionKeys[ activeTree ][ nodeId ];

                      dispatch( setParams( uid, {
                          selectionKeys: newSelectionKeys
                      } ) );
                  }

                  // deselect if selected
                  if ( selectedElementId === itemToUse.id ) {
                      onSelectElement?.( undefined );
                  }
              }

              // clean up form
              if ( formPanelKey ) {
                  const formPanelKeyWithoutPrefix = formPanelKey.replace( parentUid + '_', '' ),
                        panelProps                = { ...getPanelProps( parentUid ) };

                  if ( panelProps ) {
                      // delete props of form
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];

                      // delete error of form
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey ];
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey + '_attributesForm' ];

                      setPanelProps( parentUid, panelProps );
                  }

                  // delete elusive data of form
                  dispatch( clearElusiveData( formPanelKey ) );
                  dispatch( clearElusiveData( formPanelKey + '_attributesForm' ) );
              }
          },
          buildNodeError                                          = ( nodeId : string, dataSetToUse : MapDataSet ) => {
              const errorMsg : React.ReactNode[] = [],
                    title                        = __( 'YN.base.frontend.searchTree.error.title', {
                        type: metaModel[ 'CFG' ].getItemType( 'MapDataSet' )!.label,
                        name: String( dataSetToUse.data.name )
                    } ),
                    messages : React.ReactNode[] = [];

              if ( elusiveDataToSet.current?.errorData?.count !== undefined && elusiveDataToSet.current?.errorData.tooltip && elusiveDataToSet.current?.errorData.message ) {
                  if ( !dataSetToUse.rootDataDescriptor?.dataObject ) {
                      const message      = __( 'YN.base.frontend.searchTree.error.rootDataDescriptorMissing.message' ),
                            messageTitle = __( 'YN.base.frontend.searchTree.error.rootDataDescriptorMissing.title' ),
                            messageText  = __( 'YN.base.frontend.searchTree.error.rootDataDescriptorMissing.text' );

                      elusiveDataToSet.current.errorData.count++;

                      if ( !errorMsg.length ) {
                          elusiveDataToSet.current.errorData.tooltip.push(
                              <div className="yn-tooltip-header2">
                                  {title}
                              </div>
                          );
                      }
                      elusiveDataToSet.current.errorData.tooltip.push(
                          <div key={`error_title_${elusiveDataToSet.current.errorData.count}`} className="yn-tooltip-subheader">
                              {messageTitle}
                          </div>
                      );
                      elusiveDataToSet.current.errorData.tooltip.push(
                          <React.Fragment key={`error_message_${elusiveDataToSet.current.errorData.count}`}>
                              {messageText}
                          </React.Fragment>
                      );

                      if ( errorMsg.length ) {
                          messages.push(
                              <hr key={`error_ruler_${elusiveDataToSet.current.errorData.count}`} className="yn-error-ruler"/>
                          );
                      }
                      messages.push(
                          <div key={`error_title_${elusiveDataToSet.current.errorData.count}`} className="yn-error-subtitle">
                              {messageTitle}
                          </div>
                      );
                      messages.push(
                          <div key={`error_message_${elusiveDataToSet.current.errorData.count}`} className="yn-error-message">
                              {messageText}
                          </div>
                      );

                      errorMsg.push( message );
                  }

                  if ( !dataSetToUse.locationDataDescriptor?.dataObject ) {
                      const message      = __( 'YN.base.frontend.searchTree.error.locationDataDescriptorMissing' ),
                            messageTitle = __( 'YN.base.frontend.searchTree.error.locationDataDescriptorMissing.title' ),
                            messageText  = __( 'YN.base.frontend.searchTree.error.locationDataDescriptorMissing.text' );

                      elusiveDataToSet.current.errorData.count++;

                      if ( !errorMsg.length ) {
                          elusiveDataToSet.current.errorData.tooltip.push(
                              <div className="yn-tooltip-header2">
                                  {title}
                              </div>
                          );
                      }
                      elusiveDataToSet.current.errorData.tooltip.push(
                          <div key={`error_title_${elusiveDataToSet.current.errorData.count}`} className="yn-tooltip-subheader">
                              {messageTitle}
                          </div>
                      );
                      elusiveDataToSet.current.errorData.tooltip.push(
                          <React.Fragment key={`error_message_${elusiveDataToSet.current.errorData.count}`}>
                              {messageText}
                          </React.Fragment>
                      );

                      if ( errorMsg.length ) {
                          messages.push(
                              <hr key={`error_ruler_${elusiveDataToSet.current.errorData.count}`} className="yn-error-ruler"/>
                          );
                      }
                      messages.push(
                          <div key={`error_title_${elusiveDataToSet.current.errorData.count}`} className="yn-error-subtitle">
                              {messageTitle}
                          </div>
                      );
                      messages.push(
                          <div key={`error_message_${elusiveDataToSet.current.errorData.count}`} className="yn-error-message">
                              {messageText}
                          </div>
                      );

                      if ( errorMsg.length ) {
                          errorMsg.push( <br/> );
                      }
                      errorMsg.push( message );
                  }

                  if ( messages.length ) {
                      elusiveDataToSet.current.errorData.message.push(
                          <>
                              <div className="yn-error-subtitle">
                                  {title}
                              </div>
                              <hr className="yn-error-ruler"/>
                              <div className="yn-error-suberror">
                                  {messages}
                              </div>
                          </>
                      );
                  }
              }

              return <>
                  <Tooltip target={`#${nodeId}_error`} showDelay={500} position="top">
                      {errorMsg}
                  </Tooltip>
                  <span
                      id={`${nodeId}_error`}
                      className="yn-icon pi pi-exclamation-circle yn-error"
                      style={{ fontSize: '1rem', width: '1.6rem', height: '1.6rem' }}
                  />
              </>;
          },
          buildNodeMarker                                         = ( nodeId : string, type : 'connectTo' | 'filter' | 'location' | 'root' | 'time', dataSetName? : string ) => {
              const retVal : React.ReactNode[] = [];

              let icon = 'target';

              switch ( type ) {
                  case 'connectTo':
                      icon = 'line_start_circle';
                      break;

                  case 'filter':
                      icon = 'filter_alt';
                      break;

                  case 'location':
                      icon = 'pin_drop';
                      break;

                  case 'root':
                      icon = 'target';
                      break;

                  case 'time':
                      icon = 'history';
                      break;
              }

              retVal.push( <Tooltip target={`#${nodeId}_${type}_tooltip`} showDelay={500} position="top">
                  {__( `YN.base.frontend.searchTree.nodeMarker.${type}.tooltip`, { dataSetName: dataSetName ?? '' } )}
              </Tooltip> );

              retVal.push( <span
                  id={`${nodeId}_${type}_tooltip`}
                  className="yn-icon material-symbols-outlined yn-blue"
                  style={{ fontSize: '16px', width: '1.6rem', height: '1.6rem' }}
              >
                  {icon}
              </span> );

              return retVal;
          },
          buildNodeAddButton                                      = ( nodeId : string, dataObjectToUse : DataStructure, dataStructureToUse : DataStructure ) => {
              const buttonId   = `${uid}_${nodeId}_addButton`,
                    getContent = () => {
                        const dataObjectSchema   = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                              dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                              parentDataObject   = getParentDataObject( dataObjectToUse.id, dataStructureToUse );

                        if ( dataObjectItemType ) {

                            // TODO: handle circular referenceSets

                            const allowedReferenceSets   = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                  definedReferenceSets   = ( [] as ReferenceSet[] ).concat(
                                          parentDataObject
                                              ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                              : []
                                      )
                                      .concat(
                                          dataObjectToUse.dataStructure?.length
                                              ? dataObjectToUse.dataStructure
                                                  .reduce(
                                                      ( innerRetVal, childDataStructureToUse ) => {
                                                          innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                          return innerRetVal;
                                                      },
                                                      [] as ReferenceSet[]
                                                  )
                                              : []
                                      ),
                                  includeReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal.push( rSet.id );
                                          }

                                          return innerRetVal;
                                      },
                                      [] as number[]
                                  ),
                                  allowedReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal[ rSet.id ] = rSet.destItemType.id === dataObjectItemType.id ? 'dest' : 'src';
                                          }

                                          return innerRetVal;
                                      },
                                      {} as { [ referenceSetId : number ] : 'dest' | 'src' }
                                  );

                            return getAddItemDialog( {
                                uid                   : uid + '-AddItemReferenceDialog_button_content',
                                dataContext,
                                schema                : dataObjectSchema,
                                includeReferenceSets  : includeReferenceSetIds,
                                allowedReferenceSetIds: allowedReferenceSetIds,
                                targetItemId          : dataObjectToUse.origId,
                                addMode               : 'new',
                                rawMode               : false,
                                showAdditionalRawNames: false,
                                useThisLabel          : false,
                                onAddNode             : addDataObject.bind( null, dataObjectToUse, dataStructureToUse ),
                                onHide                : () => {
                                    nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                },
                                onInteraction         : () => {
                                    nodeButtons.current[ buttonId ].justInteracted = true;
                                    ignoreSelectionChange.current = true;
                                }
                            } );
                        }
                        else {
                            return <></>;
                        }
                    };

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon="pi pi-plus-circle yn-add"
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={() => {
                      const innerRetVal = !!getContent();

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  onCollapse={( _event, isOutsideClicked ) => {
                      const innerRetVal = !nodeButtons.current[ buttonId ].justInteracted && isOutsideClicked;

                      nodeButtons.current[ buttonId ].justInteracted = false;

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  content={getContent}
              />;
          },
          buildNodeDeleteButton                                   = ( nodeId : string, itemToUse : Item ) => {
              const buttonId = `${uid}_${nodeId}_deleteButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon={`pi ${itemToUse.isDeleted() ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={( event ) => {
                      let innerRetVal : boolean = true;

                      if ( event.altKey || itemToUse.isDeleted() ) {
                          deleteOrUndeleteNode( nodeId, itemToUse );

                          innerRetVal = false;
                      }

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}

                  content={!itemToUse.isDeleted()
                      ? <Panel
                          key={`${buttonId}_panel`}
                          id={`${buttonId}_panel`}

                          style={{ width: '25rem' }}
                          className="yn-confirm-option-panel"
                      >
                          <label>
                              <span className="pi pi-question-circle yn-blue"></span>
                              {__( 'YN.base.designer.tree.item.delete.text', { itemKey: itemToUse.key, itemName: itemToUse.name, itemDisplayAttributeValue: itemToUse.displayAttributeValue } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  deleteOrUndeleteNode( nodeId, itemToUse );

                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                      : null}
              />;
          },
          buildNodeVisibilityButton                               = ( nodeId : string, itemToUse : Item, visible : boolean ) => {
              const buttonId = `${uid}_${nodeId}_visibilityButton`;

              return <Button
                  key={buttonId}
                  id={buttonId}

                  icon={`yn-blue pi pi-eye${!visible ? '-slash' : ''}`}
                  rounded={true}
                  text={true}
                  outlined={true}
                  onClick={() => {
                      ignoreSelectionChange.current = true;

                      setMapDataSetActive( itemToUse, !visible );
                  }}
              />;
          },
          buildMapDataSetNode                                     = ( dataSetToUse : MapDataSet ) : ExtendTreeNode => {
              const nodeId      = `MDS_${dataSetToUse.data.key.replaceAll( ':', '-' )}`,
                    dataSetItem = global.data[ dataContext ].items[ dataSetToUse.data.id ],
                    retVal      = {
                        id        : nodeId,
                        key       : nodeId,
                        label     : dataSetToUse.data.name,
                        selectable: !dataSetItem.isDeleted(),
                        className : 'yn-dataset-node',
                        icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${dataSetItem.itemType.icon}` ),
                        data      : {
                            itemId               : dataSetItem.id,
                            isDeleted            : dataSetItem.isDeleted(),
                            rootDataObjectId     : dataSetToUse.rootDataDescriptor?.dataObject?.origId,
                            connectToDataObjectId: dataSetToUse.connectToDataDescriptor?.dataObject?.origId,
                            locationDataObjectId : dataSetToUse.locationDataDescriptor?.dataObject?.origId,
                            timeDataObjectId     : dataSetToUse.timeDataDescriptor?.dataObject?.origId,
                            buttons              : [],
                            filters              : []
                        }
                    } as ExtendTreeNode;

              // error marker
              if ( !dataSetToUse.rootDataDescriptor?.dataObject || !dataSetToUse.locationDataDescriptor?.dataObject ) {
                  retVal.data.buttons.push( buildNodeError( nodeId, dataSetToUse ) );
              }

              // visibility toggle
              retVal.data.buttons.push( buildNodeVisibilityButton( nodeId, dataSetItem, dataSetToUse.data.active ) );

              // delete buton
              if ( dataSets.filter( dS => dS.query.id === dataSetToUse.query.id && dS.condition?.id === dataSetToUse.condition?.id ).length > 1 ) {
                  retVal.data.buttons.push( buildNodeDeleteButton( nodeId, dataSetItem ) );
              }

              return retVal;
          },
          buildDataObjectNode                                     = ( dataObjectToUse : DataStructure, queryToUse : Query, parsedCondition : ParsedCondition | undefined, dataSetsToUse : MapDataSet[] ) : ExtendTreeNode | undefined => {
              const dataStructureToUse = queryToUse.dataStructure

              let retVal : ExtendTreeNode | undefined = undefined;

              if ( dataStructureToUse ) {
                  const schema                      = ( dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        parentDataObject            = getParentDataObject( dataObjectToUse.id, dataStructureToUse ),
                        dataObjectItem              = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                        dataObjectItemDeleted       = dataObjectItem.isDeleted(),
                        dataObjectKey               = dataObjectToUse.id,
                        dataObjectSchema            = ( schema ?? dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        dataObjectItemType          = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                        dataObjectReferenceSet      = parentDataObject
                            ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )?.[ 0 ]
                            : undefined,
                        dataObjectReferenceTypeName = dataObjectToUse.reference?.referenceConfiguration
                            ? Object.keys( dataObjectToUse.reference.referenceConfiguration )[ 0 ] as string
                            : undefined;

                  if ( dataObjectItemType ) {
                      const nodeId               = `DO_${dataObjectItem.key.replaceAll( ':', '-' )}`,
                            allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                            definedReferenceSets = ( [] as ReferenceSet[] ).concat(
                                    parentDataObject
                                        ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                        : []
                                )
                                .concat(
                                    dataObjectToUse.dataStructure?.length
                                        ? dataObjectToUse.dataStructure
                                            .reduce(
                                                ( innerRetVal, childDataStructureToUse ) => {
                                                    innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                    return innerRetVal;
                                                },
                                                [] as ReferenceSet[]
                                            )
                                        : []
                                );

                      let attributePath : string,
                          dataDescriptor : DataDescriptor | undefined,
                          fieldValueType : PropertyValueType,
                          fieldItemType : string;

                      retVal = {
                          id        : nodeId,
                          key       : nodeId,
                          dataObject: dataObjectToUse,
                          label     : dataObjectItemType.label,
                          selectable: !dataObjectItemDeleted,
                          className : 'yn-dataobject-node',
                          icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${dataObjectItemType.icon}` ),
                          data      : {
                              itemId        : dataObjectItem.id,
                              isDeleted     : dataObjectItemDeleted,
                              isRootFor     : dataSetsToUse.reduce(
                                  ( acc, dataSet ) => {
                                      if ( dataObjectItem.id === dataSet.rootDataDescriptor?.dataObject?.item.id ) {
                                          acc.push( dataSet.data.id );
                                      }

                                      return acc;
                                  },
                                  [] as number[]
                              ),
                              isConnectToFor: dataSetsToUse.reduce(
                                  ( acc, dataSet ) => {
                                      if ( dataObjectItem.id === dataSet.connectToDataDescriptor?.dataObject?.item.id ) {
                                          acc.push( dataSet.data.id );
                                      }

                                      return acc;
                                  },
                                  [] as number[]
                              ),
                              isLocationFor : dataSetsToUse.reduce(
                                  ( acc, dataSet ) => {
                                      if ( dataObjectItem.id === dataSet.locationDataDescriptor?.dataObject?.item.id ) {
                                          acc.push( dataSet.data.id );
                                      }

                                      return acc;
                                  },
                                  [] as number[]
                              ),
                              isTimeFor     : dataSetsToUse.reduce(
                                  ( acc, dataSet ) => {
                                      if ( dataObjectItem.id === dataSet.timeDataDescriptor?.dataObject?.item.id ) {
                                          acc.push( dataSet.data.id );
                                      }

                                      return acc;
                                  },
                                  [] as number[]
                              ),
                              buttons       : [],
                              filters       : []
                          }
                      };

                      // special label for referenced Address, Message or Image item
                      if ( ( dataObjectItemType.name === 'Address' || dataObjectItemType.name === 'Message' || dataObjectItemType.name === 'Image' ) && ( dataObjectReferenceSet || dataObjectReferenceTypeName ) ) {
                          retVal.label = `${_.upperFirst( dataExtraction.extractIsForReferenceTypeName( dataObjectReferenceSet?.referenceTypeLabel ?? dataObjectReferenceTypeName ?? '' ) )} (${dataObjectItemType.label})`;
                      }

                      // root marker
                      if ( retVal.data.isRootFor?.length ) {
                          retVal.data.buttons.push( buildNodeMarker( nodeId, 'root', retVal.data.isRootFor.map( ( id : number ) => global.data[ dataContext ]?.items[ id ]?.name ).join( ', ' ) ) );
                      }

                      // connectTo marker
                      if ( retVal.data.isConnectToFor?.length ) {
                          retVal.data.buttons.push( buildNodeMarker( nodeId, 'connectTo', retVal.data.isConnectToFor.map( ( id : number ) => global.data[ dataContext ]?.items[ id ]?.name ).join( ', ' ) ) );
                      }

                      // location marker
                      if ( retVal.data.isLocationFor?.length ) {
                          retVal.data.buttons.push( buildNodeMarker( nodeId, 'location', retVal.data.isLocationFor.map( ( id : number ) => global.data[ dataContext ]?.items[ id ]?.name ).join( ', ' ) ) );
                      }

                      // time marker
                      if ( retVal.data.isTimeFor?.length ) {
                          retVal.data.buttons.push( buildNodeMarker( nodeId, 'time', retVal.data.isTimeFor.map( ( id : number ) => global.data[ dataContext ]?.items[ id ]?.name ).join( ', ' ) ) );
                      }

                      // filter marker
                      if ( Object.keys( parsedCondition ?? {} ).length ) {
                          retVal.data.buttons.push( buildNodeMarker( nodeId, 'filter' ) );
                      }

                      // add button
                      if ( !readOnly && allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                          retVal.data.buttons.push( buildNodeAddButton( dataObjectKey, dataObjectToUse, dataStructureToUse ) );
                      }
                      // delete/restore button
                      if ( !readOnly && parentDataObject ) {
                          retVal.data.buttons.push( buildNodeDeleteButton( nodeId, dataObjectItem ) );
                      }

                      // filters
                      fieldValueType = 'STRING';
                      fieldItemType = 'StringField';
                      attributePath = 'item.key';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          if ( ( dataUtils.getDataValueType( dataDescriptor, dataObjectSchema ) ?? 'STRING' ) as PropertyValueType === 'INTEGER' ) {
                              fieldValueType = 'INTEGER';
                              fieldItemType = 'IntegerField';
                          }

                          retVal.data.filters.push( {
                              type               : 'item',
                              key                : 'key',
                              label              : getItemKeyDisplayName( dataObjectItemType.id, dataObjectSchema ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                              dataObjectId       : dataObjectToUse.origId,
                              attributePath,
                              dataDescriptorAlias: dataDescriptor.alias,
                              valueType          : fieldValueType,
                              operator           : parsedCondition?.[ dataDescriptor.alias ]?.operator,
                              value              : parsedCondition?.[ dataDescriptor.alias ]?.value,
                              conditionIds       : parsedCondition?.[ dataDescriptor.alias ]?.ids
                          } );
                      }

                      fieldValueType = 'STRING';
                      fieldItemType = 'StringField';
                      attributePath = 'item.name';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          retVal.data.filters.push( {
                              type               : 'item',
                              key                : 'name',
                              label              : getItemNameDisplayName( dataObjectItemType.id, dataObjectSchema ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                              dataObjectId       : dataObjectToUse.origId,
                              attributePath,
                              dataDescriptorAlias: dataDescriptor.alias,
                              valueType          : fieldValueType,
                              operator           : parsedCondition?.[ dataDescriptor.alias ]?.operator,
                              value              : parsedCondition?.[ dataDescriptor.alias ]?.value,
                              conditionIds       : parsedCondition?.[ dataDescriptor.alias ]?.ids
                          } );
                      }

                      // item properties - get possible fields for all properties
                      const propertySets = metaModel[ dataObjectSchema ].getPropertySetsForItemType( dataObjectItemType.id );

                      propertySets.sort(
                          ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                      );

                      for ( const propertySet of propertySets ) {
                          const params : FilterConfigSet = {
                              valueSetId: propertySet.valueSetId,
                              valueUnit : propertySet.valueUnit
                          };

                          attributePath = `property.${propertySet.name}`;
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              if ( propertySet.isEnumerated ) {
                                  fieldItemType = 'EnumField';
                              }
                              else if ( propertySet.valueSetId ) {
                                  fieldItemType = 'ValueSetField';
                              }
                              else {
                                  fieldItemType = fieldTypeToItemTypeMapping[ propertySet.valueType ];
                              }

                              retVal.data.filters.push( {
                                  type               : 'item',
                                  key                : propertySet.name,
                                  label              : propertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                                  dataObjectId       : dataObjectToUse.origId,
                                  attributePath,
                                  dataDescriptorAlias: dataDescriptor.alias,
                                  valueType          : propertySet.valueType,
                                  params,
                                  operator           : parsedCondition?.[ dataDescriptor.alias ]?.operator,
                                  value              : parsedCondition?.[ dataDescriptor.alias ]?.value,
                                  conditionIds       : parsedCondition?.[ dataDescriptor.alias ]?.ids
                              } );
                          }
                      }
                  }
              }

              return retVal;
          },
          buildDataStructureTree                                  = ( queryToUse : Query, conditionToUse : Condition | undefined, dataSetsToUse : MapDataSet[] ) : BuildTreeData => {
              const retVal : BuildTreeData = {
                        treeData    : [],
                        expandedKeys: {}
                    },
                    aliases : string[]     = [];

              let parsedCondition : ParsedConditions = {},
                  notParsable                        = false;

              if ( queryToUse.dataStructure ) {
                  if ( conditionToUse?.operator === 'and' && Array.isArray( conditionToUse.leftExpression ) ) {
                      for ( const subCondition of conditionToUse.leftExpression ) {
                          const ids : number[] = [];

                          let dataObjectOrigId : number = 0,
                              alias : string            = '',
                              operator : FilterMatchMode | undefined,
                              value : string            = '';

                          // subCondition operator is 'and' OR 'or' condition?
                          // -> test all subSubCondition operators are not 'and' OR 'or' and have the same alias and aliases are unique
                          if ( ( subCondition.operator === 'and' || subCondition.operator === 'or' ) && Array.isArray( subCondition.leftExpression ) ) {
                              const subOperator = subCondition.operator === 'and'
                                  ? ' && '
                                  : ' || ';

                              operator = 'custom';

                              for ( const subSubCondition of subCondition.leftExpression ) {
                                  if ( subSubCondition.operator === 'and' || subSubCondition.operator === 'or' ) {
                                      notParsable = true;
                                      break;
                                  }
                                  else if ( subSubCondition.leftExpressionDataDescriptor?.dataObject?.origId && subSubCondition.leftExpressionDataDescriptor?.alias ) {
                                      if ( !alias ) {
                                          if ( !aliases.includes( subSubCondition.leftExpressionDataDescriptor.alias ) ) {
                                              aliases.push( subSubCondition.leftExpressionDataDescriptor.alias );
                                          }
                                          else {
                                              notParsable = true;
                                              break;
                                          }

                                          dataObjectOrigId = subSubCondition.leftExpressionDataDescriptor.dataObject.origId;

                                          alias = subSubCondition.leftExpressionDataDescriptor.alias;
                                      }
                                      else if ( alias !== subSubCondition.leftExpressionDataDescriptor.alias ) {
                                          notParsable = true;
                                          break;
                                      }

                                      if ( subSubCondition.operator ) {
                                          if ( value ) {
                                              value += subOperator;
                                          }
                                          value += getValueStringFromMatchModeAndValue( subSubCondition.operator, subSubCondition.rightExpression );
                                      }

                                      if ( subSubCondition.id ) {
                                          ids.push( subSubCondition.id );
                                      }
                                  }
                                  else {
                                      notParsable = true;
                                      break;
                                  }
                              }
                          }
                          // test aliases are unique
                          else if ( subCondition.leftExpressionDataDescriptor?.dataObject?.origId && subCondition.leftExpressionDataDescriptor?.alias ) {
                              if ( !aliases.includes( subCondition.leftExpressionDataDescriptor.alias ) ) {
                                  aliases.push( subCondition.leftExpressionDataDescriptor.alias );

                                  if ( subCondition.id ) {
                                      ids.push( subCondition.id );
                                  }

                                  dataObjectOrigId = subCondition.leftExpressionDataDescriptor.dataObject?.origId;

                                  alias = subCondition.leftExpressionDataDescriptor.alias;

                                  operator = ( subCondition.operator !== 'and' && subCondition.operator !== 'or' )
                                      ? subCondition.operator
                                      : undefined;

                                  value = typeof subCondition.rightExpression === 'string'
                                      ? subCondition.rightExpression
                                      : '';
                              }
                              else {
                                  notParsable = true;
                              }
                          }
                          else {
                              notParsable = true;
                          }

                          if ( notParsable ) {
                              break;
                          }

                          parsedCondition[ dataObjectOrigId ] = {
                              ...parsedCondition[ dataObjectOrigId ],
                              [ alias ]: {
                                  ids,
                                  operator,
                                  value
                              }
                          }
                      }

                      if ( notParsable ) {
                          parsedCondition = {};
                      }
                  }

                  const buildNodeDataRecursive = ( dataStructureToProcess : DataStructure ) => {
                      const nodeData = buildDataObjectNode( dataStructureToProcess, queryToUse, parsedCondition[ dataStructureToProcess.origId ], dataSetsToUse );

                      if ( nodeData ) {
                          retVal.expandedKeys[ nodeData.key ] = true;

                          // process children only if not deleted
                          if ( !global.data[ dataContext ].items[ dataStructureToProcess.origId ].isDeleted() && dataStructureToProcess.dataStructure?.length ) {
                              nodeData.children = [];

                              for ( const childDataStructureToProcess of dataStructureToProcess.dataStructure ) {
                                  const childNodeData = buildNodeDataRecursive( childDataStructureToProcess );

                                  if ( childNodeData ) {
                                      retVal.expandedKeys[ childNodeData.key ] = true;
                                      nodeData.children.push( childNodeData );
                                  }
                              }

                              // sort by label
                              nodeData.children.sort( ( childNodeData1, childNodeData2 ) => {
                                  if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                      return 1;
                                  }
                                  else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } )
                          }
                      }

                      return nodeData;
                  };

                  const nodeData = buildNodeDataRecursive( queryToUse.dataStructure );

                  if ( nodeData ) {
                      retVal.treeData.push( nodeData );
                  }
              }

              return retVal;
          },
          treeData                                                = useMemo(
              () => {
                  const newExpandedKeys : AllProps[ 'expandedKeys' ]   = {},
                        newSelectionKeys : AllProps[ 'selectionKeys' ] = { ...( lastParams.current?.selectionKeys ?? selectionKeys ) };

                  let retVal        = elusiveData?.treeData,
                      newActiveTree = activeTree;

                  // full (re-)build
                  if (
                      !retVal
                      || (
                          lastParams.current
                          && (
                              dataSets !== lastParams.current.dataSets
                              || dataContext !== lastParams.current.dataContext
                              || globalDataVersions !== lastParams.current.globalDataVersions
                              || rootItem !== lastParams.current.rootItem
                              || language !== lastParams.current.language
                              || messageCatalogTimestamp !== lastParams.current.messageCatalogTimestamp
                              || metaModelTimestamp !== lastParams.current.metaModelTimestamp
                          )
                      )
                  ) {
                      retVal = {};

                      elusiveDataToSet.current = {
                          ...elusiveDataToSet.current,
                          errorData: {
                              count  : 0,
                              tooltip: [],
                              message: []
                          }
                      };

                      // collect unique query and condition combinations with all dataSets using them
                      for ( const dataSet of dataSets ) {
                          const queryConditionId = `${dataSet.query.id}${dataSet.condition ? `_${dataSet.condition.id}` : ''}`;

                          if ( !Object.keys( retVal ).includes( queryConditionId ) ) {
                              retVal[ queryConditionId ] = {
                                  order   : Object.keys( retVal ).length,
                                  dataSets: {
                                      [ dataSet.data.key ]: {
                                          node: buildMapDataSetNode( dataSet ),
                                          dataSet
                                      }
                                  },
                                  tree    : []
                              };
                          }
                          else if ( !( Object.keys( retVal[ queryConditionId ].dataSets ) as string[] ).includes( dataSet.data.key ) ) {
                              retVal[ queryConditionId ].dataSets = {
                                  ...retVal[ queryConditionId ].dataSets,
                                  [ dataSet.data.key ]: {
                                      node: buildMapDataSetNode( dataSet ),
                                      dataSet
                                  }
                              };
                          }
                      }

                      // build trees for unique query and condition combinations
                      for ( const queryConditionId of Object.keys( retVal ) as string[] ) {
                          const result = buildDataStructureTree(
                              Object.values( retVal[ queryConditionId ].dataSets )[ 0 ].dataSet.query,
                              Object.values( retVal[ queryConditionId ].dataSets )[ 0 ].dataSet.condition,
                              Object.values( retVal[ queryConditionId ].dataSets ).map( dS => dS.dataSet )
                          );

                          retVal[ queryConditionId ].tree = result.treeData;
                          newExpandedKeys[ queryConditionId ] = result.expandedKeys;

                          if ( !Object.keys( newSelectionKeys[ queryConditionId ] ?? {} ).length ) {
                              newSelectionKeys[ queryConditionId ] = { [ result.treeData[ 0 ].key ]: true };
                          }
                      }

                      if ( !newActiveTree || !retVal[ newActiveTree ] ) {
                          newActiveTree = ( Object.keys( retVal ) as string[] )[ 0 ];
                      }

                      paramsToSet.current = {
                          expandedKeys : newExpandedKeys,
                          selectionKeys: newSelectionKeys,
                          activeTree   : newActiveTree
                      };
                  }

                  return retVal;
              },
              [
                  changedDataSets.current,
                  dataContext,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp,
                  rootItem
              ]
          ),
          addMapDataSet                                           = useCallback(
              ( mapDataSetName : string, rootDataObjectSchema? : Schema, rootDataObjectItemType? : string ) => {
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  if ( rootItem && mapDataSetName && ( activeTree || ( rootDataObjectSchema && rootDataObjectItemType ) ) ) {
                      const treeDataSets                          = !rootDataObjectItemType
                                ? Object.values( treeData[ activeTree ?? '' ].dataSets ).map( dS => dS.dataSet )
                                : undefined,
                            activeStatusId                        = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                            mapDataSetItemTypeId                  = metaModel[ 'CFG' ].getItemType( 'MapDataSet' )!.id,
                            mDSOrderPropertySetId                 = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( mapDataSetItemTypeId, 'Order' )!.id,
                            mDSSearchGroupPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( mapDataSetItemTypeId, 'SearchGroup' )!.id,
                            mDSGetsDataForMapSearchReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( mapDataSetItemTypeId, 'MapSearch', 'getsDataFor', 'src' )![ 0 ].id,
                            cIsFilterForMDSReferenceSetId         = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Condition', mapDataSetItemTypeId, 'isFilterFor', 'src' )![ 0 ].id,
                            queryItemTypeId                       = metaModel[ 'CFG' ].getItemType( 'Query' )!.id,
                            qSchemaPropertySetId                  = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( queryItemTypeId, 'Schema' )!.id,
                            qTypePropertySetId                    = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( queryItemTypeId, 'Type' )!.id,
                            qGetsDataForMDSReferenceSetId         = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( queryItemTypeId, mapDataSetItemTypeId, 'getsDataFor', 'src' )![ 0 ].id,
                            dataObjectItemTypeId                  = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                            dOObjectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                            dOObjectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                            dOIsRootOfQReferenceSetId             = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, queryItemTypeId, 'isRootOf', 'src' )![ 0 ].id,
                            newMapDataSet                         = dispatch( addItem(
                                dataContext,
                                {
                                    statusId  : activeStatusId,
                                    itemTypeId: mapDataSetItemTypeId,
                                    key       : `${rootItem!.key}:${uuidV4().substring( 0, 8 )}`,
                                    name      : mapDataSetName,
                                    properties: [
                                        {
                                            propertySetId: mDSOrderPropertySetId,
                                            value        : treeDataSets
                                                ? Math.max( ...treeDataSets.map( dS => dS.data.order ) ) + 1
                                                : 1,
                                            statusId     : activeStatusId
                                        },
                                        {
                                            propertySetId: mDSSearchGroupPropertySetId,
                                            value        : treeDataSets?.[ 0 ]?.data.searchGroup
                                                ?? mapDataSetName,
                                            statusId     : activeStatusId
                                        }
                                    ]
                                },
                                'CFG',
                                additionalElusiveDataContexts
                            ) );

                      // activate new MapDataSet
                      onSelectElement?.( newMapDataSet.id );

                      dispatch( addReference(
                          dataContext,
                          {
                              referenceSetId: mDSGetsDataForMapSearchReferenceSetId,
                              statusId      : activeStatusId,
                              srcItemId     : newMapDataSet.id,
                              destItemId    : rootItem.id
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      // add query and condition
                      if ( treeDataSets?.[ 0 ] ) {
                          // reference existing Query
                          dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: qGetsDataForMDSReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : treeDataSets[ 0 ].query.id,
                                  destItemId    : newMapDataSet.id
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          // reference existing Condition
                          if ( treeDataSets[ 0 ].condition?.id ) {
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: cIsFilterForMDSReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : treeDataSets[ 0 ].condition.id,
                                      destItemId    : newMapDataSet.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }
                      else if ( rootDataObjectSchema && rootDataObjectItemType ) {
                          const newQuery          = dispatch( addItem(
                                    dataContext,
                                    {
                                        statusId  : activeStatusId,
                                        itemTypeId: queryItemTypeId,
                                        key       : `${rootItem!.key}:${uuidV4().substring( 0, 8 )}`,
                                        name      : mapDataSetName,
                                        properties: [
                                            {
                                                propertySetId: qSchemaPropertySetId,
                                                value        : rootDataObjectSchema.toLowerCase(),
                                                statusId     : activeStatusId
                                            },
                                            {
                                                propertySetId: qTypePropertySetId,
                                                value        : 'DataStructure',
                                                statusId     : activeStatusId
                                            }
                                        ]
                                    },
                                    'CFG',
                                    additionalElusiveDataContexts
                                ) ),
                                newRootDataObject = dispatch( addItem(
                                    dataContext,
                                    {
                                        statusId  : activeStatusId,
                                        itemTypeId: dataObjectItemTypeId,
                                        key       : `${rootItem!.key}:${uuidV4().substring( 0, 8 )}`,
                                        name      : mapDataSetName,
                                        properties: [
                                            {
                                                propertySetId: dOObjectSchemaPropertySetId,
                                                value        : rootDataObjectSchema.toLowerCase(),
                                                statusId     : activeStatusId
                                            },
                                            {
                                                propertySetId: dOObjectTypePropertySetId,
                                                value        : rootDataObjectItemType,
                                                statusId     : activeStatusId
                                            }
                                        ]
                                    },
                                    'CFG',
                                    additionalElusiveDataContexts
                                ) );

                          // activate new search group
                          dispatch( setParam( uid, 'activeTree', `${newQuery.id}` ) );

                          // reference new Query
                          dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: qGetsDataForMDSReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : newQuery.id,
                                  destItemId    : newMapDataSet.id
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          // reference new root DataObject to new Query
                          dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: dOIsRootOfQReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : newRootDataObject.id,
                                  destItemId    : newQuery.id
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                      }
                  }
              },
              [
                  activeTree,
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItem,
                  treeData,
                  uid
              ]
          ),
          addFilterCondition                                      = useCallback(
              ( filter : FilterDefinition ) => {
                  const treeDataSets = Object.values( treeData[ activeTree ?? '' ].dataSets ).map( dS => dS.dataSet );

                  if ( rootItem && treeDataSets.length ) {
                      const activeStatusId                                         = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                            conditionItemTypeId                                    = metaModel[ 'CFG' ].getItemType( 'Condition' )!.id,
                            cLeftExpressionPropertySetId                           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'LeftExpression' )!.id,
                            cOperatorPropertySetId                                 = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'Operator' )!.id,
                            cIsFilterForMapDataSetReferenceSetId                   = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, 'MapDataSet', 'isFilterFor', 'src' )![ 0 ].id,
                            cIsLeftExpressionForCReferenceSetId                    = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, conditionItemTypeId, 'isLeftExpressionFor', 'src' )![ 0 ].id,
                            dataObjectProvidesDataForLeftExpressionCReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', conditionItemTypeId, 'providesDataForLeftExpression', 'src' )![ 0 ].id,
                            newCondition                                           = dispatch( addItem(
                                dataContext,
                                {
                                    statusId  : activeStatusId,
                                    itemTypeId: conditionItemTypeId,
                                    key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                    name      : 'filter condition',
                                    properties: [
                                        {
                                            propertySetId: cLeftExpressionPropertySetId,
                                            value        : filter.attributePath,
                                            statusId     : activeStatusId
                                        },
                                        {
                                            propertySetId: cOperatorPropertySetId,
                                            value        : filter.params?.valueSetId
                                                ? 'equals'
                                                : getDefaultFilterMatchMode( filter.valueType ),
                                            statusId     : activeStatusId
                                        }
                                    ]
                                },
                                'CFG',
                                additionalElusiveDataContexts
                            ) );

                      // reference dataObject to new condition
                      dispatch( addReference(
                          dataContext,
                          {
                              referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                              statusId      : activeStatusId,
                              srcItemId     : filter.dataObjectId,
                              destItemId    : newCondition.id
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      let rootConditionId : number | undefined = treeDataSets[ 0 ].condition?.id;

                      // add root condition if not
                      if ( !rootConditionId ) {
                          const rootCondition = dispatch( addItem(
                              dataContext,
                              {
                                  statusId  : activeStatusId,
                                  itemTypeId: conditionItemTypeId,
                                  key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                  name      : 'filter root condition',
                                  properties: [
                                      {
                                          propertySetId: cOperatorPropertySetId,
                                          value        : 'and',
                                          statusId     : activeStatusId
                                      }
                                  ]
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          rootConditionId = rootCondition.id;

                          dispatch( setParam( uid, 'activeTree', `${treeDataSets[ 0 ].query.id}_${rootCondition.id}` ) );

                          // add to all MapDataSets belonging to this search group
                          for ( const dataSet of treeDataSets ) {
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: cIsFilterForMapDataSetReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : rootCondition.id,
                                      destItemId    : dataSet.data.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      // reference new condition to root condition
                      dispatch( addReference(
                          dataContext,
                          {
                              referenceSetId: cIsLeftExpressionForCReferenceSetId,
                              statusId      : activeStatusId,
                              srcItemId     : newCondition.id,
                              destItemId    : rootConditionId
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );
                  }
              },
              [
                  activeTree,
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItem,
                  treeData,
                  uid
              ]
          ),
          changeFilterCondition                                   = useCallback(
              ( filter : FilterDefinition, matchMode : FilterMatchMode, value : any ) => {
                  const activeStatusId                                         = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        deletedStatusId                                        = metaModel[ 'CFG' ].getStatus( 'deleted' )!.id,
                        conditionItemTypeId                                    = metaModel[ 'CFG' ].getItemType( 'Condition' )!.id,
                        cLeftExpressionPropertySet                             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'LeftExpression' )!,
                        cOperatorPropertySet                                   = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'Operator' )!,
                        cRightExpressionPropertySet                            = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'RightExpression' )!,
                        cIsLeftExpressionForCReferenceSetId                    = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, conditionItemTypeId, 'isLeftExpressionFor', 'src' )![ 0 ].id,
                        dataObjectProvidesDataForLeftExpressionCReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', conditionItemTypeId, 'providesDataForLeftExpression', 'src' )![ 0 ].id;

                  // it's a non-custom condition
                  if ( matchMode !== 'custom' ) {

                      if ( !filter.conditionIds ) {
                          // TODO: error
                      }

                      // was non-custom condition - just update
                      else if ( filter.operator !== 'custom' && filter.conditionIds.length === 1 ) {
                          const conditionItem = global.data[ dataContext ].items[ filter.conditionIds[ 0 ] ];

                          if ( conditionItem ) {
                              const properties : ItemAttributesSettable['properties'] = {
                                  [ cOperatorPropertySet.propertyType.id ]: {
                                      statusId: activeStatusId,
                                      value   : matchMode
                                  }
                              };

                              if ( matchMode === 'empty' || matchMode === 'notEmpty' ) {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: deletedStatusId
                                  };
                              }
                              else {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: activeStatusId,
                                      value   : value
                                  };
                              }

                              dispatch( updateItem(
                                  dataContext,
                                  conditionItem.id,
                                  {
                                      properties
                                  },
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      // was custom condition - get parent and update, delete conditions
                      else if ( filter.operator === 'custom' || filter.conditionIds.length > 1 ) {
                          const parentConditionId   = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, filter.conditionIds[ 0 ], undefined, undefined, false )?.[ 0 ]?.destItemId,
                                parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                          if ( parentConditionItem ) {
                              const properties : ItemAttributesSettable['properties'] = {
                                  [ cLeftExpressionPropertySet.propertyType.id ]: {
                                      statusId: activeStatusId,
                                      value   : filter.attributePath
                                  },
                                  [ cOperatorPropertySet.propertyType.id ]      : {
                                      statusId: activeStatusId,
                                      value   : matchMode
                                  }
                              };

                              if ( matchMode === 'empty' || matchMode === 'notEmpty' ) {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: deletedStatusId
                                  };
                              }
                              else {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: activeStatusId,
                                      value   : value
                                  };
                              }
                              // delete conditions
                              for ( const conditionId of filter.conditionIds ) {
                                  dispatch( deleteItem(
                                      dataContext,
                                      conditionId,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      true,
                                      false
                                  ) );
                              }

                              // update parent condition
                              dispatch( updateItem(
                                  dataContext,
                                  parentConditionItem.id,
                                  {
                                      properties
                                  },
                                  additionalElusiveDataContexts
                              ) );

                              // reference dataObject to parent condition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : filter.dataObjectId,
                                      destItemId    : parentConditionItem.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }
                  }

                  // it's a custom condition
                  else {
                      const joinOperator  = getCustomFilterMode( value ).toLowerCase(),
                            customFilters = getCustomFilters( value, filter.valueType );

                      let parentConditionItem : Item | undefined

                      if ( !filter.conditionIds ) {
                          // TODO: error
                      }

                      // was non-custom condition - get condition as parent
                      else if ( filter.operator !== 'custom' && filter.conditionIds.length === 1 ) {
                          parentConditionItem = global.data[ dataContext ].items[ filter.conditionIds[ 0 ] ];
                      }

                      // was custom condition - get parent condition, delete conditions
                      else if ( filter.operator === 'custom' || filter.conditionIds.length > 1 ) {
                          const parentConditionId = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, filter.conditionIds[ 0 ], undefined, undefined, false )?.[ 0 ]?.destItemId;

                          parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                          if ( parentConditionItem ) {
                              // delete conditions
                              for ( const conditionId of filter.conditionIds ) {
                                  dispatch( deleteItem(
                                      dataContext,
                                      conditionId,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      true,
                                      false
                                  ) );
                              }
                          }
                      }

                      if ( parentConditionItem && customFilters.length && rootItem ) {
                          dispatch( updateItem(
                              dataContext,
                              parentConditionItem.id,
                              {
                                  properties: {
                                      [ cLeftExpressionPropertySet.propertyType.id ] : {
                                          statusId: deletedStatusId
                                      },
                                      [ cOperatorPropertySet.propertyType.id ]       : {
                                          statusId: activeStatusId,
                                          value   : joinOperator
                                      },
                                      [ cRightExpressionPropertySet.propertyType.id ]: {
                                          statusId: deletedStatusId
                                      }
                                  }
                              },
                              additionalElusiveDataContexts
                          ) );

                          for ( const customFilter of customFilters ) {
                              const condition = dispatch( addItem(
                                  dataContext,
                                  {
                                      statusId  : activeStatusId,
                                      itemTypeId: conditionItemTypeId,
                                      key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                      name      : 'filter condition',
                                      properties: [
                                          {
                                              propertySetId: cLeftExpressionPropertySet.id,
                                              statusId     : activeStatusId,
                                              value        : filter.attributePath
                                          },
                                          {
                                              propertySetId: cOperatorPropertySet.id,
                                              statusId     : activeStatusId,
                                              value        : customFilter.matchMode
                                          }
                                      ].concat(
                                          customFilter.matchMode !== 'empty' && customFilter.matchMode !== 'notEmpty'
                                              ? {
                                                  propertySetId: cRightExpressionPropertySet.id,
                                                  statusId     : activeStatusId,
                                                  value        : customFilter.value
                                              }
                                              : []
                                      )
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              // reference dataObject to condition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : filter.dataObjectId,
                                      destItemId    : condition.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              // reference condition to parentCondition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: cIsLeftExpressionForCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : condition.id,
                                      destItemId    : parentConditionItem.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }

                      }
                  }
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItem
              ]
          ),
          removeFilterCondition                                   = useCallback(
              ( filter : FilterDefinition ) => {
                  const treeDataSets = Object.values( treeData[ activeTree ?? '' ].dataSets ).map( dS => dS.dataSet );

                  if ( treeDataSets.length ) {
                      const cIsLeftExpressionForCReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'Condition', 'isLeftExpressionFor', 'src' )![ 0 ].id,
                            rootConditionId : number | undefined = treeDataSets[ 0 ].condition?.id;

                      if ( !filter.conditionIds ) {
                          // TODO: error
                      }

                      // was non-custom condition - just delete
                      else if ( filter.operator !== 'custom' && filter.conditionIds.length === 1 ) {
                          dispatch( deleteItem(
                              dataContext,
                              filter.conditionIds[ 0 ],
                              true,
                              additionalElusiveDataContexts,
                              false,
                              true,
                              false
                          ) );
                      }

                      // was custom condition - get parent and delete
                      else if ( filter.operator === 'custom' || filter.conditionIds.length > 1 ) {
                          const parentConditionId   = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, filter.conditionIds[ 0 ], undefined, undefined, false )?.[ 0 ]?.destItemId,
                                parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                          if ( parentConditionItem ) {
                              // delete conditions
                              for ( const conditionId of filter.conditionIds ) {
                                  dispatch( deleteItem(
                                      dataContext,
                                      conditionId,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      true,
                                      false
                                  ) );
                              }

                              dispatch( deleteItem(
                                  dataContext,
                                  parentConditionItem.id,
                                  true,
                                  additionalElusiveDataContexts,
                                  false,
                                  true,
                                  false
                              ) );
                          }
                      }

                      const subExpressionReferences = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, undefined, rootConditionId, undefined, false );

                      // remove root condition if there are no other conditions left
                      if ( rootConditionId && ( !subExpressionReferences || subExpressionReferences.length === 0 ) ) {
                          dispatch( deleteItem(
                              dataContext,
                              rootConditionId,
                              true,
                              additionalElusiveDataContexts,
                              false,
                              true,
                              false
                          ) );
                      }

                      dispatch( setParam( uid, 'activeTree', `${treeDataSets[ 0 ].query.id}` ) );
                  }
              },
              [
                  activeTree,
                  additionalElusiveDataContexts,
                  dataContext,
                  treeData
              ]
          ),
          onTreeScroll                                            = useCallback(
              _.debounce(
                  () => {
                      if ( activeTree ) {
                          const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

                          if ( scrollElement ) {
                              dispatch( setParam(
                                  uid,
                                  'treeScrollPositions',
                                  {
                                      ...treeScrollPositions,
                                      [ activeTree ]: {
                                          top : scrollElement.scrollTop ?? 0,
                                          left: scrollElement.scrollLeft ?? 0
                                      }
                                  }
                              ) );
                          }
                      }
                  },
                  250
              ),
              [
                  activeTree,
                  treeScrollPositions,
                  uid
              ]
          ),
          footer                                                  = useMemo(
              () => {
                  const layoutComponents : ToolbarLayoutComponents = {
                      left : [],
                      right: []
                  };

                  const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

                  return <Toolbar
                      start={toolbarComponents.left}
                      className="yn-panel-toolbar shadow-4"
                      style={{ zIndex: 5 }}
                  />;
              },
              [
                  uid
              ]
          ),
          onContextMenuSelectionChange                            = useCallback(
              ( event : TreeSelectionEvent ) => {
                  setContextMenuNodeKey( event.value + '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onContextMenu                                           = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  if ( !readOnly ) {
                      setContextMenuNode( event.node as ExtendTreeNode );
                      contextMenu.current?.show( event.originalEvent );
                  }
              },
              [
                  readOnly
              ]
          ),
          contextMenuModel                                        = useMemo(
              () => {
                  const retVal : MenuItem[] = [];

                  if ( contextMenuNode && activeTree ) {
                      const treeDataSets  = Object.values( treeData[ activeTree ].dataSets ).map( dS => dS.dataSet ),
                            dataStructure = treeDataSets[ 0 ].query.dataStructure!;

                      // dataObject node
                      if ( contextMenuNode.dataObject ) {
                          const dataObject = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.id === contextMenuNode.dataObject!.id ) as DataStructure | undefined;

                          if ( dataObject ) {
                              const parentDataObject      = getParentDataObject( dataObject.id, dataStructure ),
                                    dataObjectItem        = global.data[ dataContext ].items[ dataObject.origId ],
                                    dataObjectItemDeleted = dataObjectItem.isDeleted(),
                                    dataObjectSchema      = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                    dataObjectItemType    = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                              if ( dataObjectItemType ) {
                                  const allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                        definedReferenceSets = ( [] as ReferenceSet[] ).concat(
                                                parentDataObject
                                                    ? getReferenceSetsForDataObject( dataObject, dataStructure, dataObjectSchema )
                                                    : []
                                            )
                                            .concat(
                                                dataObject.dataStructure?.length
                                                    ? dataObject.dataStructure
                                                        .reduce(
                                                            ( innerRetVal, childDataStructureToUse ) => {
                                                                innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructure, dataObjectSchema ) );

                                                                return innerRetVal;
                                                            },
                                                            [] as ReferenceSet[]
                                                        )
                                                    : []
                                            );

                                  // add button
                                  if ( allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                                      const buttonId   = `${uid}_${dataObject.id}_addButton`,
                                            menuItemId = `${dataObject.id}_addItem`;

                                      retVal.push( {
                                          id     : menuItemId,
                                          label  : __( 'YN.base.designer.tree.item.add.title' ),
                                          icon   : 'pi pi-plus-circle yn-add',
                                          data   : {
                                              buttonId
                                          },
                                          command: ( e ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                                      } );
                                  }

                                  // delete/restore button
                                  if ( parentDataObject ) {
                                      const buttonId = `${uid}_${dataObject.id}_deleteButton`;

                                      if ( retVal.length ) {
                                          retVal.push( { separator: true } );
                                      }

                                      retVal.push( {
                                          id     : `${dataObject.id}_${dataObjectItemDeleted ? 'undeleteItem' : 'deleteItem'}`,
                                          label  : dataObjectItemDeleted ? __( 'YN.base.designer.tree.item.undelete.title' ) : __( 'YN.base.designer.tree.item.delete.title' ),
                                          icon   : `pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`,
                                          data   : {
                                              buttonId
                                          },
                                          command: ( e : MenuItemCommandEvent ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent )
                                      } );
                                  }

                                  // use as root or location for MapDataSet
                                  for ( const dataSet of treeDataSets ) {
                                      let buttonAdded : boolean = false;

                                      // set as root for dataSet
                                      // if no location dataDescriptor or location DataObject is a child of this DataObject
                                      if (
                                          dataObject.id !== dataSet.rootDataDescriptor?.dataObject?.id
                                          && (
                                              !dataSet.connectToDataDescriptor?.dataObject
                                              || getDataStructurePath( dataSet.connectToDataDescriptor.dataObject.id, dataStructure ).includes( dataObject.id )
                                          )
                                          && (
                                              !dataSet.locationDataDescriptor?.dataObject
                                              || getDataStructurePath( dataSet.locationDataDescriptor.dataObject.id, dataStructure ).includes( dataObject.id )
                                          )
                                          && (
                                              !dataSet.timeDataDescriptor?.dataObject
                                              || getDataStructurePath( dataSet.timeDataDescriptor.dataObject.id, dataStructure ).includes( dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataObject.id}_addAsRoot`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addAsRootFor.label', { dataSetName: dataSet.data.name } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '16px',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          target
                                                      </span>,
                                              command: ( e ) => {
                                                  setRoot( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as connectTo for dataSet
                                      // if dataObjectItemType has a COORDINATE property and no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.connectToDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'COORDINATE' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataObject.id}_addAsConnectTo`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addAsConnectToFor.label', { dataSetName: dataSet.data.name } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          line_start_circle
                                                      </span>,
                                              command: ( e ) => {
                                                  setConnectTo( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as location for dataSet
                                      // if dataObjectItemType has a COORDINATE property and no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.locationDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'COORDINATE' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataObject.id}_addAsLocation`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addAsLocationFor.label', { dataSetName: dataSet.data.name } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          pin_drop
                                                      </span>,
                                              command: ( e ) => {
                                                  setLocation( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as time for dataSet
                                      // if dataObjectItemType has a TIMESTAMP property no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.timeDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'TIMESTAMP' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataObject.id}_addAsTime`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addAsTimeFor.label', { dataSetName: dataSet.data.name } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          history
                                                      </span>,
                                              command: ( e ) => {
                                                  setTime( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }
                                  }
                              }
                          }
                      }

                      // mapDataset node
                      else {
                          const dataSet = dataSets.find( dS => dS.data.id === contextMenuNode.data.itemId ) as MapDataSet | undefined;

                          if ( dataSet ) {
                              const dataObjects = extractDataObjectsFromDataStructure( dataStructure );

                              for ( const dataObject of dataObjects ) {
                                  const dataObjectSchema   = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                        dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                                  let buttonAdded : boolean = false;

                                  if ( dataObjectItemType ) {

                                      // set as root for dataSet
                                      // if no location dataDescriptor or location DataObject is a child of this DataObject
                                      if (
                                          dataObject.id !== dataSet.rootDataDescriptor?.dataObject?.id
                                          && (
                                              !dataSet.locationDataDescriptor?.dataObject
                                              || getDataStructurePath( dataSet.locationDataDescriptor.dataObject.id, dataStructure ).includes( dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataSet.data.id}_addAsRoot`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addRoot.label', { dataObjectName: dataObjectItemType.label } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '16px',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          target
                                                      </span>,
                                              command: ( e ) => {
                                                  setRoot( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as connectTo for dataSet
                                      // if dataObjectItemType has a COORDINATE property and no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.connectToDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'COORDINATE' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataSet.data.id}_addAsConnectTo`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addConnectTo.label', { dataObjectName: dataObjectItemType.label } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          line_start_circle
                                                      </span>,
                                              command: ( e ) => {
                                                  setConnectTo( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as location for dataSet
                                      // if dataObjectItemType has a COORDINATE property and no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.locationDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'COORDINATE' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataSet.data.id}_addAsLocation`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addLocation.label', { dataObjectName: dataObjectItemType.label } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          pin_drop
                                                      </span>,
                                              command: ( e ) => {
                                                  setLocation( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }

                                      // set as time for dataSet
                                      // if dataObjectItemType has a TIMESTAMP property no root dataDescriptor or root DataObject is a parent of this DataObject
                                      if (
                                          dataObject.id !== dataSet.timeDataDescriptor?.dataObject?.id
                                          && dataObjectItemType.propertySets.map( pS => pS.valueType ).some( pVT => pVT === 'TIMESTAMP' )
                                          && (
                                              !dataSet.rootDataDescriptor?.dataObject
                                              || getDataStructurePath( dataObject.id, dataStructure ).includes( dataSet.rootDataDescriptor.dataObject.id )
                                          )
                                      ) {
                                          const menuItemId = `${dataSet.data.id}_addAsTime`;

                                          if ( !buttonAdded ) {
                                              buttonAdded = true;

                                              if ( retVal.length ) {
                                                  retVal.push( { separator: true } );
                                              }
                                          }

                                          retVal.push( {
                                              id     : menuItemId,
                                              label  : __( 'YN.base.frontend.searchTree.addTime.label', { dataObjectName: dataObjectItemType.label } ),
                                              icon   : <span
                                                  className="yn-icon material-symbols-outlined yn-blue"
                                                  style={{
                                                      fontSize   : '1.2rem',
                                                      marginRight: '.5rem'
                                                  }}>
                                                          history
                                                      </span>,
                                              command: ( e ) => {
                                                  setTime( dataObject.origId, dataSet.data.id );
                                              }
                                          } );
                                      }
                                  }
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  activeTree,
                  contextMenuNode,
                  dataContext,
                  dataSets,
                  treeData,
                  uid
              ]
          ),
          onAddMapDataSetDialogShow                               = useCallback(
              ( createNew : boolean = true ) => {
                  setNewMapDataSetName( '' );
                  setNewMapDataSetSchema( 'DATA' );
                  setNewMapDataSetItemType( '' );
                  setNewMapDataSetType( createNew ? 'new' : 'forCurrent' );
                  setShowAddMapDataSetDialog( true );
              },
              [
                  'onlyOnce'
              ]
          ),
          onAddMapDataSetDialogHide                               = useCallback(
              () => {
                  setShowAddMapDataSetDialog( false );
              },
              [
                  'onlyOnce'
              ]
          ),
          onAddMapDataSetDialogOk                                 = useCallback(
              ( newMapDataSetTypeToUse : 'new' | 'forCurrent', newMapDataSetNameToUse : string, newMapDataSetSchemaToUse : Schema, newMapDataSetItemTypeToUse : string ) => {
                  addMapDataSet(
                      newMapDataSetNameToUse,
                      newMapDataSetTypeToUse === 'new'
                          ? newMapDataSetSchemaToUse
                          : undefined,
                      newMapDataSetTypeToUse === 'new'
                          ? newMapDataSetItemTypeToUse
                          : undefined
                  );

                  onAddMapDataSetDialogHide();
              },
              [
                  additionalElusiveDataContexts,
                  onAddMapDataSetDialogHide
              ]
          ),
          onTreeNodeToggleClick                                   = useCallback(
              ( key : string, itemId : number ) => {
                  const nodeItem = global.data[ dataContext ].items[ itemId ];

                  // deleted nodes could not be selected
                  if ( ( !nodeItem || nodeItem.statusId !== -1 ) && activeTree ) {
                      const newSelectionKeys = {
                          ...selectionKeys,
                          [ activeTree ]: { ...selectionKeys?.[ activeTree ] }
                      };

                      if ( newSelectionKeys[ activeTree ][ key ] ) {
                          delete newSelectionKeys[ activeTree ][ key ];
                      }
                      else {
                          newSelectionKeys[ activeTree ][ key ] = true;
                      }

                      dispatch( setParams( uid, {
                          selectionKeys: newSelectionKeys
                      } ) );
                  }
              },
              [
                  activeTree,
                  dataContext,
                  selectionKeys,
                  uid
              ]
          ),
          onMarkDataSets                                          = useCallback(
              ( dataSetIds? : number[] ) => {
                  // remove current
                  const elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-dataset-node` );

                  let index = 0;

                  if ( elements?.length ) {
                      while ( elements[ index ] ) {
                          elements[ index++ ].classList.remove( 'yn-searchtree-node-marked' );
                      }
                  }

                  if ( dataSetIds?.length ) {
                      for ( const id of dataSetIds ) {
                          const item    = global.data[ dataContext ].items[ id ],
                                element = document.getElementById( `MDS_${item?.key.replaceAll( ':', '-' )}` )?.closest( '.yn-dataset-node' );

                          element?.classList.add( 'yn-searchtree-node-marked' );
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onMarkDataObjects                                       = useCallback(
              ( dataObjectId? : number[] ) => {
                  // remove current
                  const elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-dataobject-node` );

                  let index = 0;

                  if ( elements?.length ) {
                      while ( elements[ index ] ) {
                          elements[ index++ ].classList.remove( 'yn-searchtree-node-marked' );
                      }
                  }

                  if ( dataObjectId?.length ) {
                      for ( const id of dataObjectId ) {
                          const item    = global.data[ dataContext ].items[ id ],
                                element = document.getElementById( `DO_${item?.key.replaceAll( ':', '-' )}` )?.closest( '.yn-dataobject-node' );

                          element?.classList.add( 'yn-searchtree-node-marked' );
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          filterDropdownItemTemplate                              = useCallback(
              ( option : DropdownProps ) => {
                  let retVal : React.ReactNode = null;

                  if ( option ) {
                      const filter = option as FilterDefinition;

                      retVal = <div
                          style={{
                              display   : 'flex',
                              alignItems: 'center'
                          }}
                      >
                          {getIconSpan( filter.icon, { style: { display: 'inline-block', marginRight: '.5rem' } } )}
                          {filter.label}
                      </div>;
                  }

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          filterDropdownValueTemplate                             = useCallback(
              ( option : DropdownProps, dropdownProps : DropdownProps ) => {
                  let retVal : React.ReactNode = dropdownProps.placeholder;

                  if ( option ) {
                      const filter = option as FilterDefinition;

                      retVal = <div
                          style={{
                              display   : 'flex',
                              alignItems: 'center'
                          }}
                      >
                          {getIconSpan( filter.icon, { style: { display: 'inline-block', marginRight: '.5rem' } } )}
                          {filter.label}
                      </div>;
                  }

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          onAddFilter                                             = useCallback(
              ( event : DropdownChangeEvent ) => {
                  addFilterCondition( event.value as FilterDefinition );
              },
              [
                  addFilterCondition
              ]
          ),
          onChangeFilter                                          = useCallback(
              _.debounce(
                  ( filter : FilterDefinition, key : string, field : string, matchMode : FilterMatchMode, value : any ) => {
                      changeFilterCondition( filter, matchMode, value );
                  },
                  250
              ),
              [
                  changeFilterCondition
              ]
          ),
          onRemoveFilter                                          = useCallback(
              ( filter : FilterDefinition ) => {
                  removeFilterCondition( filter );
              },
              [
                  removeFilterCondition
              ]
          ),
          treeNodeTemplate                                        = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  // handle DataObject nodes
                  const nodeItem        = global.data[ dataContext ].items[ nodeData.data.itemId ?? 0 ],
                        formPanelErrors = allElusiveData?.[ `${parentUid}_form-${nodeData.data.itemId ?? 0}` ]?.errorData?.count > 0;

                  let content : React.ReactNode = null,
                      tooltip : React.ReactNode = null;

                  // build node content if selected
                  if ( activeTree && selectionKeys?.[ activeTree ]?.[ nodeData.key ] ) {
                      const addableFilters = nodeData.data.filters.filter( f => f.operator === undefined ),
                            setFilters     = nodeData.data.filters.filter( f => f.operator !== undefined );

                      content = <div
                          key={`${nodeData.key}_body`}
                          className="yn-node-content"
                      >
                          <div
                              key={`${nodeData.key}_scrollContainer`}
                              id={`${nodeData.key}_scrollContainer`}
                              className="yn-treenode-container"
                          >
                              {addableFilters.length &&
                                  <Dropdown
                                      id={`${nodeData.key}_addFilterDropdown`}
                                      inputId={`${nodeData.key}_addFilterDropdown_i`}
                                      onChange={onAddFilter}
                                      options={addableFilters}
                                      dataKey="key"
                                      disabled={readOnly}
                                      placeholder={__( 'YN.base.frontend.searchTree.filter.add' )}
                                      valueTemplate={filterDropdownValueTemplate}
                                      itemTemplate={filterDropdownItemTemplate}
                                      style={{ width: '100%' }}
                                  />
                              }
                              {setFilters.map( filter => {
                                  return <FieldWrapper
                                      key={`${uid}_${filter.key}_filter`}
                                      uid={`${uid}_${filter.key}_filter`}
                                      label={filter.label}
                                      labelPosition="top"
                                      infoTextLayout="hidden"
                                      isDirty={false}
                                      autoComplete={false}
                                      disabled={readOnly}
                                  >
                                      <div
                                          style={{
                                              display: 'flex'
                                          }}
                                      >
                                          <Button
                                              onClick={onRemoveFilter.bind( null, filter )}
                                              icon="pi pi-times"
                                              className="p-button-rounded p-button-danger p-button-text"
                                              style={{
                                                  alignSelf  : 'center',
                                                  flex       : '0 0 auto',
                                                  marginRight: '.5rem'
                                              }}
                                          />
                                          <FilterField
                                              fieldKey={`${uid}_${filter.key}_filter_i`}
                                              field={`${uid}_${filter.key}_filter_i`}

                                              value={filter.value ?? ''}
                                              valueType={filter.valueType}
                                              matchMode={filter.operator}

                                              showMatchModeSelector={true}
                                              markDirty={false}

                                              params={{
                                                  ...filter.params,
                                                  schema,
                                                  timezone              : defaultBaseRequestParameters.timezone,
                                                  rawMode               : false,
                                                  showAdditionalRawNames: false
                                              }}

                                              onChange={onChangeFilter.bind( null, filter )}
                                          />
                                      </div>
                                  </FieldWrapper>;
                              } )}
                          </div>
                      </div>;
                  }

                  // build node tooltip
                  if ( nodeData.tooltip ) {
                      tooltip = <Tooltip target={`#${nodeData.key}`} showDelay={500} position="top">
                          {nodeData.tooltip}
                      </Tooltip>;
                  }

                  if ( activeTree && selectionKeys?.[ activeTree ]?.[ nodeData.key ] ) {
                      if ( !classNames.includes( 'yn-searchtree-node-expanded' ) ) {
                          classNames.push( 'yn-searchtree-node-expanded' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-searchtree-node-expanded' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if ( nodeItem?.isDeleted() && nodeItem?.isModified() ) {
                      if ( !classNames.includes( 'yn-deleted' ) ) {
                          classNames.push( 'yn-deleted' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-deleted' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if ( !( nodeItem?.isDeleted() && nodeItem?.isModified() ) && nodeItem?.isModified() ) {
                      labelClassNames.push( 'yn-dirty' );
                  }

                  if ( formPanelErrors || ( !nodeData.dataObject && ( !nodeData.data.rootDataObjectId || !nodeData.data.locationDataObjectId ) ) ) {
                      labelClassNames.push( 'yn-error' );
                  }

                  if ( nodeData.data.itemId === selectedElementId ) {
                      if ( !classNames.includes( 'yn-searchtree-node-selected' ) ) {
                          classNames.push( 'yn-searchtree-node-selected' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-searchtree-node-selected' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  nodeData.className = classNames.join( ' ' );

                  // build node
                  return <>
                      {tooltip}
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                      <div
                          key={`${nodeData.key}_header`}
                          className="flex flex-column flex-auto overflow-hidden yn-searchtree-header"
                          draggable="false"
                          onClick={() => {
                              if ( !ignoreSelectionChange.current ) {
                                  onSelectElement?.( nodeData.data.itemId );
                              }

                              ignoreSelectionChange.current = false;
                          }}
                          onMouseEnter={() => {
                              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                              if ( nodeData.data.isRootFor || nodeData.data.isConnectToFor || nodeData.data.isLocationFor || nodeData.data.isTimeFor ) {
                                  onMarkDataSets( _.uniq( ( [] as number[] )
                                      .concat( nodeData.data.isRootFor ?? [] )
                                      .concat( nodeData.data.isConnectToFor ?? [] )
                                      .concat( nodeData.data.isLocationFor ?? [] )
                                      .concat( nodeData.data.isTimeFor ?? [] ) )
                                  );
                              }
                              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                              else if ( nodeData.data.rootDataObjectId || nodeData.data.connectToDataObjectId || nodeData.data.locationDataObjectId || nodeData.data.timeDataObjectId ) {
                                  onMarkDataObjects( _.uniq( ( [] as number[] )
                                      .concat( nodeData.data.rootDataObjectId ?? [] )
                                      .concat( nodeData.data.connectToDataObjectId ?? [] )
                                      .concat( nodeData.data.locationDataObjectId ?? [] )
                                      .concat( nodeData.data.timeDataObjectId ?? [] ) )
                                  );
                              }
                          }}
                          onMouseLeave={() => {
                              onMarkDataSets( undefined );
                              onMarkDataObjects( undefined );
                          }}
                      >
                          <div key={`${nodeData.key}`} id={`${nodeData.key}`} className="yn-treenode-header">
                              <span key="label" className={labelClassNames.join( ' ' )}>{nodeData.label ?? ''}</span>
                              {nodeData.data.buttons}
                          </div>
                      </div>
                      {content}
                  </>;
              },
              [
                  activeTree,
                  dataContext,
                  filterDropdownItemTemplate,
                  filterDropdownValueTemplate,
                  language,
                  onAddFilter,
                  onChangeFilter,
                  onMarkDataObjects,
                  onMarkDataSets,
                  onRemoveFilter,
                  onSelectElement,
                  readOnly,
                  selectedElementId,
                  selectionKeys
              ]
          ),
          treeTogglerTemplate                                     = useCallback(
              ( nodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  if ( nodeData.id?.indexOf( 'DO_' ) === 0 ) {
                      const icon = IconUtils.getJSXIcon(
                          selectionKeys?.[ activeTree ?? '' ]?.[ nodeData.key ?? '' ]
                              ? <ChevronDownIcon/>
                              : <ChevronRightIcon/>,
                          {},
                          {}
                      );

                      return <Button
                          className={`yn-tree-toggler p-tree-toggler p-link p-unselectable-text ${nodeData.selectable === false ? 'yn-disabled' : ''}`}
                          icon={icon}
                          onClick={() => {
                              onTreeNodeToggleClick( String( nodeData.key ?? '' ), nodeData.data.itemId );
                          }}>
                      </Button>
                  }
                  else {
                      return <span style={{ display: 'inline-block', width: '.5rem' }}></span>;
                  }
              },
              [
                  activeTree,
                  onTreeNodeToggleClick,
                  selectionKeys
              ]
          ),
          onTabContainerTabChange                                 = useCallback(
              ( event : TabViewTabChangeEvent ) => {
                  if ( !readOnly && event.index === 0 ) {
                      onAddMapDataSetDialogShow( true );
                  }
                  else {
                      const orderedTreeDataKeys = ( Object.keys( treeData ) as string[] ).sort( ( k1, k2 ) => {
                          if ( treeData[ k1 ].order > treeData[ k2 ].order ) {
                              return 1;
                          }
                          else if ( treeData[ k1 ].order < treeData[ k2 ].order ) {
                              return -1;
                          }
                          else {
                              return 0;
                          }
                      } );

                      dispatch( setParam( uid, 'activeTree', orderedTreeDataKeys[ event.index - ( readOnly ? 0 : 1 ) ] ) );

                      // activate new MapSearch
                      onSelectElement?.( rootItemId );
                  }
              },
              [
                  onAddMapDataSetDialogShow,
                  readOnly,
                  treeData,
                  uid
              ]
          ),
          onTabContainerScroll                                    = useCallback(
              _.debounce(
                  ( event : React.UIEvent<HTMLDivElement> ) => {
                      dispatch( setParam( uid, 'tabContainerScrollPosition', ( event.target as HTMLElement ).scrollLeft ) );
                  },
                  250
              ),
              [
                  uid
              ]
          ),
          onNewMapDataSetNameSet                                  = useCallback(
              ( value : string | undefined ) => {
                  setNewMapDataSetName( value ? value : '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onNewMapDataSetSchemaSet                                = useCallback(
              ( value : string ) => {
                  setNewMapDataSetSchema( value as Schema );
              },
              [
                  'onlyOnce'
              ]
          ),
          onNewMapDataSetItemTypeSet                              = useCallback(
              ( value : string ) => {
                  setNewMapDataSetItemType( value );
              },
              [
                  'onlyOnce'
              ]
          ),
          addMapDataSetDialog                                     = useMemo(
              () => {
                  let retVal = <></>;

                  if ( showAddMapDataSetDialog ) {
                      const okDisabled                  = Boolean( !newMapDataSetName || ( newMapDataSetType === 'new' && !newMapDataSetItemType ) ),
                            style : React.CSSProperties = {
                                width           : '33rem',
                                minWidth        : '33rem',
                                [ '--min-width' ]: '33rem',
                                minHeight       : '25vh'
                            };

                      // only one selectable schemata and not set -> set it
                      if ( selectableSchemata.length === 1 && newMapDataSetSchema !== selectableSchemata[ 0 ] ) {
                          setNewMapDataSetSchema( selectableSchemata[ 0 ] );
                          return;
                      }

                      retVal = <Dialog
                          key={`${uid}_addMapDataSetDialog`}
                          id={`${uid}_addMapDataSetDialog`}

                          visible={true}
                          onHide={onAddMapDataSetDialogHide}
                          focusOnShow={false}
                          closeOnEscape={false}
                          className="yn-form-dialog"
                          style={style}
                          appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
                          header={__( `YN.base.frontend.searchTree.addDialog.${newMapDataSetType}.title` )}
                          footer={<div>
                              <Button
                                  id={`${uid}_okButton`}
                                  key="okButton"
                                  label={__( 'YN.base.common.ok' )}
                                  className="p-button-outlined mr-3"
                                  disabled={okDisabled}
                                  onClick={onAddMapDataSetDialogOk.bind( null, newMapDataSetType, newMapDataSetName, newMapDataSetSchema, newMapDataSetItemType )}
                              />
                              <Button
                                  id={`${uid}_cancelButton`}
                                  key="cancelButton"
                                  label={__( 'YN.base.common.cancel' )}
                                  className="p-button-secondary p-button-outlined"
                                  onClick={onAddMapDataSetDialogHide}
                              />
                          </div>}
                      >
                          <StringField
                              uid={`${uid}_nameField`}
                              label={__( 'YN.base.frontend.searchTree.addDialog.name.label' )}
                              value={newMapDataSetName}
                              style={{ width: '30rem' }}
                              labelPosition="top"
                              infoTextLayout="hidden"
                              markDirty={false}
                              onChange={onNewMapDataSetNameSet}
                              autoComplete={false}
                          />
                          {newMapDataSetType === 'new' &&
                              <FieldWrapper
                                  uid={`${uid}_itemTypeField`}
                                  label={__( 'YN.base.frontend.searchTree.addDialog.itemType.label' )}
                                  labelPosition="top"
                                  infoTextLayout="hidden"
                                  isDirty={false}
                                  autoComplete={false}
                              >
                                  {selectableSchemata.length > 1 &&
                                      <SchemaDropdown
                                          uid={uid}
                                          value={newMapDataSetSchema}
                                          onChange={onNewMapDataSetSchemaSet}

                                          style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                                      />
                                  }
                                  <ItemTypeDropdown
                                      uid={uid}

                                      schema={newMapDataSetSchema}
                                      value={newMapDataSetItemType}
                                      rawMode={false}
                                      showAdditionalRawNames={false}
                                      markDirty={false}
                                      onChange={onNewMapDataSetItemTypeSet}
                                      inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                                  />
                              </FieldWrapper>
                          }
                      </Dialog>;
                  }

                  return retVal;
              },
              [
                  language,
                  messageCatalogTimestamp,
                  newMapDataSetItemType,
                  newMapDataSetName,
                  newMapDataSetSchema,
                  newMapDataSetType,
                  onAddMapDataSetDialogHide,
                  onAddMapDataSetDialogOk,
                  onNewMapDataSetItemTypeSet,
                  onNewMapDataSetNameSet,
                  onNewMapDataSetSchemaSet,
                  selectableSchemata,
                  showAddMapDataSetDialog,
                  uid
              ]
          ),
          tree                                                    = useMemo(
              () => {
                  let retVal = <></>;

                  if ( activeTree && treeData[ activeTree ] ) {
                      const value = Object.values( treeData[ activeTree ].dataSets ).map( dS => dS.node )
                          .concat( treeData[ activeTree ].tree );

                      retVal = <>
                          <ContextMenu
                              key={`${uid}_contextMenu`}
                              model={contextMenuModel}
                              ref={el => contextMenu.current = el}
                              onHide={() => setContextMenuNodeKey( undefined )}
                          />
                          <Tree
                              key={`${uid}_tree`}
                              id={`${uid}_tree`}

                              ref={el => treeElement.current = el?.getElement() ?? null}

                              value={value}
                              nodeTemplate={treeNodeTemplate}

                              expandedKeys={expandedKeys[ activeTree ]}
                              togglerTemplate={treeTogglerTemplate}
                              // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                              onToggle={() => {
                              }}

                              selectionMode="multiple"
                              selectionKeys={selectionKeys?.[ activeTree ]}

                              className="yn-menu-tree yn-node-content-tree yn-searchtree"

                              contextMenuSelectionKey={contextMenuNodeKey}
                              onContextMenuSelectionChange={onContextMenuSelectionChange}
                              onContextMenu={onContextMenu}

                              footer={footer}
                              pt={{
                                  container: {
                                      onScroll: onTreeScroll
                                  },
                                  footer   : {
                                      style: {
                                          margin: '1rem -1rem -1rem -1rem',
                                          width : 'calc(100% + 2rem)',
                                          zIndex: '10'
                                      }
                                  }
                              }}
                          />
                      </>;
                  }

                  return retVal;
              },
              [
                  activeTree,
                  contextMenuModel,
                  contextMenuNodeKey,
                  expandedKeys,
                  footer,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  onTreeScroll,
                  selectionKeys,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  uid
              ]
          ),
          treeTabPanels                                           = useMemo(
              () => {
                  const retVal : React.ReactNode[] = [],
                        orderedTreeDataKeys        = ( Object.keys( treeData ) as string[] ).sort( ( k1, k2 ) => {
                            if ( treeData[ k1 ].order > treeData[ k2 ].order ) {
                                return 1;
                            }
                            else if ( treeData[ k1 ].order < treeData[ k2 ].order ) {
                                return -1;
                            }
                            else {
                                return 0;
                            }
                        } );

                  if ( !readOnly ) {
                      retVal.push( <TabPanel
                          key={`${uid}_tabPanel_add`}
                          header={<span className="fa fa-plus yn-add"></span>}
                          className="yn-form-tabpanel"
                      /> );
                  }

                  for ( const queryConditionId of orderedTreeDataKeys ) {
                      retVal.push( <TabPanel
                          key={`${uid}_${queryConditionId}_tabPanel`}

                          header={Object.values( treeData[ queryConditionId ].dataSets )[ 0 ].dataSet.data.searchGroup ?? Object.values( treeData[ queryConditionId ].dataSets )[ 0 ].dataSet.data.name}

                          className="yn-form-tabpanel"
                      >
                          {activeTree && treeData[ activeTree ] && queryConditionId === activeTree &&
                              <>
                                  {!readOnly &&
                                      <Button
                                          label={__( 'YN.base.frontend.searchTree.dataSet.add.label' )}
                                          icon="fa fa-plus yn-add"
                                          className="p-button-sm ml-3 mr-3"
                                          style={{ alignSelf: 'start' }}
                                          rounded={false}
                                          text={true}
                                          outlined={false}
                                          onClick={() => {
                                              onAddMapDataSetDialogShow( false );
                                          }}
                                      />
                                  }
                                  {tree}
                              </>
                          }
                      </TabPanel> );
                  }

                  return retVal;
              },
              [
                  activeTree,
                  language,
                  messageCatalogTimestamp,
                  onAddMapDataSetDialogShow,
                  readOnly,
                  tree,
                  treeData,
                  uid
              ]
          ),
          content                                                 = useMemo(
              () => {
                  const activeTreeIndex = ( treeData[ activeTree ?? '' ]?.order ?? -1 ) + ( readOnly ? 0 : 1 );

                  return <>
                      {addMapDataSetDialog}
                      <TabView
                          key={`${uid}_searchTree`}
                          id={`${uid}_searchTree`}

                          activeIndex={activeTreeIndex}
                          onTabChange={onTabContainerTabChange}

                          pt={{
                              navContent: {
                                  onScroll: onTabContainerScroll
                              }
                          }}

                          className="yn-searchtree"
                      >
                          {treeTabPanels}
                      </TabView>
                  </>;
              },
              [
                  activeTree,
                  addMapDataSetDialog,
                  readOnly,
                  treeData,
                  treeTabPanels,
                  uid
              ]
          );

    lastParams.current = {
        dataContext,
        rootItem,
        globalDataVersions,
        language,
        messageCatalogTimestamp,
        metaModelTimestamp,
        rootItemId,
        selectedElementId,
        selectionKeys,
        treeData,
        uid
    };

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            dataSets,
            dataStructure: Object.values( treeData[ activeTree ?? '' ]?.dataSets ?? {} )?.[ 0 ]?.dataSet.query.dataStructure
        } )
    );

    // set the tree scroll position
    useLayoutEffect(
        () => {
            if ( activeTree ) {
                const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

                if ( scrollElement ) {
                    scrollElement.scrollLeft = treeScrollPositions?.[ activeTree ]?.left ?? 0;
                    scrollElement.scrollTop = treeScrollPositions?.[ activeTree ]?.top ?? 0;
                }
            }
        },
        [
            activeTree,
            treeScrollPositions,
            initialRendered.current
        ]
    );

    // set tab container scroll position
    useLayoutEffect(
        () => {
            const scrollElement = document.querySelector( `#${uid}_searchTree > .p-tabview-nav-container > .p-tabview-nav-content` );

            if ( scrollElement ) {
                scrollElement.scrollLeft = tabContainerScrollPosition;
            }
        },
        [
            tabContainerScrollPosition,
            uid
        ]
    );

    // update dataSets in elusiveData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { dataSets } ) );
        },
        [
            dataSets
        ]
    );

    // update treeData in elusiveData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { treeData } ) );
        },
        [
            treeData
        ]
    );

    // update paramsToSet
    useEffect(
        () => {
            if ( paramsToSet.current ) {
                dispatch( setParams( uid, paramsToSet.current ) );
            }
            paramsToSet.current = null;
        },
        [
            paramsToSet.current
        ]
    );

    // update elusiveData
    useEffect(
        () => {
            if ( elusiveDataToSet.current ) {
                dispatch( setElusiveData( uid, elusiveDataToSet.current ) );
            }
            elusiveDataToSet.current = null;
        },
        [
            elusiveDataToSet.current
        ]
    );

    return content;
} );

SearchTree.defaultProps = defaultProps;
SearchTree.displayName = 'SearchTree';


export default SearchTree;
